/* [DEAL-REUSE 2026-11-03, user] Which old HubSpot deal each card of the deal pop-up updates — ONE rule, used by the
   pop-up (DealCreationModal) AND the server (server/src/services/dealSlots.ts). ⚠ Keep the two copies identical:
   .claude/tools/deal-split/check-deal-reuse.js runs the same cases through both.

   THE BUG THIS ENDS (aciminla.com, payment 29481010). 30 Oct: $5,000 mapped as One-Time Upsell → one upsell deal.
   31 Oct: re-mapped as One-Time New Sale $1,000 + One-Time Upsell $4,000. The pop-up gave the old deal to the FIRST
   card by position (the new sale); the server gave it to the card of its own SLOT (the upsell). So BOTH cards updated
   the same deal at once, the second deal was never created, and the payment saved the same deal id twice. */

/** One stored deal per slot + part, as `payments.deal_splits` holds it: { o_so: [{ dealId, owner, amount, sv }] }. */
export type StoredDealLayout = Record<string, Array<{ dealId?: unknown } | null | undefined> | null | undefined>;

/** A card of the pop-up = a deal type (slot) and, when split, its side (part 0 / 1). */
export interface DealCard { slot: string; part: number }

/** The deal ids in `payments.deals_record_id`, in order, blanks dropped (a repeat stays). */
export function dealIdList(raw: unknown): string[] {
  return String(raw ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}

/** The same list with each deal once: what the HubSpot tag on a payment row counts and lists. */
export function uniqueDealIds(raw: unknown): string[] {
  return Array.from(new Set(dealIdList(raw)));
}

/** Two deal types on ONE HubSpot deal: one of them has no deal of its own. */
export function hasSharedDeal(raw: unknown): boolean {
  const ids = dealIdList(raw);
  return new Set(ids).size < ids.length;
}

/**
 * The old deal id each card updates (undefined = a new deal is created), in the order of `cards`.
 *   1. A card keeps the deal stored for its own slot + part.
 *   2. A card with none (a deal type new to this payment, or a split side added now) reuses an old deal no other
 *      card kept, in the stored order — so a re-map that only CHANGES the type still updates the same deal.
 *   3. Anything left gets a new deal. One deal never goes to two cards.
 * No stored layout (a payment whose deal came from before 29 Jun 2026) → by position, as before.
 */
export function assignPriorDeals(cards: DealCard[], layout: StoredDealLayout | null | undefined, priorIds: string[]): Array<string | undefined> {
  const out: Array<string | undefined> = cards.map(() => undefined);
  const used = new Set<string>();
  const take = (i: number, id: string) => { out[i] = id; used.add(id); };
  const idOf = (v: unknown) => (v == null ? '' : String(v).trim());

  if (!layout || typeof layout !== 'object' || Object.keys(layout).length === 0) {
    cards.forEach((_, i) => { const id = idOf(priorIds[i]); if (id && !used.has(id)) take(i, id); });
    return out;
  }

  cards.forEach((c, i) => {
    const parts = layout[c.slot];
    const id = Array.isArray(parts) ? idOf(parts[c.part]?.dealId) : '';
    if (id && !used.has(id)) take(i, id);
  });

  const left: string[] = [];
  const add = (v: unknown) => { const id = idOf(v); if (id && !used.has(id) && !left.includes(id)) left.push(id); };
  priorIds.forEach(add);
  Object.values(layout).forEach((parts) => { if (Array.isArray(parts)) parts.forEach((p) => add(p?.dealId)); });
  cards.forEach((_, i) => { if (out[i] === undefined && left.length > 0) take(i, left.shift() as string); });
  return out;
}
